import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref } from 'vue';
import CompanyTagsTable from '@/views/pages/companyTags/CompanyTagsTable.vue';
import CompanyTagFormDialog from '@/views/pages/companyTags/CompanyTagFormDialog.vue';
import { usePermissionComposable } from '@/composables/usePermission';
import { useTagComposable } from '@/composables/useTag';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { commitAddNotification } from '@/store/main/mutations';
import { store } from '@/store/index';
import { isValidLength } from '@/lib/rule';
import { dispatchBulkCreateCompanyTag } from '@/store/main/actionsMain';
import { AxiosError } from 'axios';
import { readAssertedCompanyId, readUserLocale } from '@/store/main/getters';
import Papa from 'papaparse';

type TagCsvRow = Record<string, string>;


export default /*#__PURE__*/_defineComponent({
  __name: 'CompanyTagsPage',
  setup(__props) {

const CURRENT_TAG_HEADER_JA = '現在のタグ名';
const NEW_TAG_HEADER_JA = '新しいタグ名';
const NEW_ON_REGISTRATION_HEADER_JA = '新規登録時は新規と入力';
const CURRENT_TAG_HEADER_EN = 'Current Tag Name';
const NEW_TAG_HEADER_EN = 'New Tag Name';
const NEW_ON_REGISTRATION_HEADER_EN = 'New on registration';
const MAX_CSV_FILE_SIZE = 2 * 1024 * 1024;

const CSV_PARSE_OPTIONS = {
  header: true,
  skipEmptyLines: true,
  transform: (value: string) => value.trim(),
  transformHeader: (header: string) => header.replace(/^\uFEFF/, '').trim(),
} as const;

const m = defineMessages({
  addTag: 'タグを作成する',
});

const formatErrorRowNumbers = (rows: number[]) =>
  [...rows].sort((a, b) => a - b).join(', ');

const notifyCsvRowsError = (rows: number[], messageKey: string) => {
  if (rows.length === 0) return;
  commitAddNotification(store, {
    messageKey,
    messageValues: { row: formatErrorRowNumbers(rows) },
    color: 'error',
  });
};

const { isLoading, tags, getCompanyTag } = useTagComposable();
const { hasPermission, PERMISSIONS: P } = usePermissionComposable();

const csvFile = ref<File | null>(null);
const showTagFormDialog = ref(false);
const showTagCsvUploadDialog = ref(false);
const uploadProgress = ref<number>(0);
const isValidFile = ref<boolean>(true);
const validEntries = ref<{ currentTagName: string; newTagName: string }[]>([]);
const csvInput = ref<HTMLInputElement | null>(null);
const isValidationCompleted = ref<boolean>(false);

const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `(${(bytes / (1024 * 1024)).toFixed(2)} MB)`;
  if (bytes >= 1024) return `(${(bytes / 1024).toFixed(2)} KB)`;
  return `(${bytes} B)`;
};

const downloadCSV = () => {
  const isJa = readUserLocale(store) === 'ja-JP';
  const currentHeader = isJa ? CURRENT_TAG_HEADER_JA : CURRENT_TAG_HEADER_EN;
  const newHeader = isJa ? NEW_TAG_HEADER_JA : NEW_TAG_HEADER_EN;
  const newOnRegistrationHeader = isJa
    ? NEW_ON_REGISTRATION_HEADER_JA
    : NEW_ON_REGISTRATION_HEADER_EN;
  const tagNames = tags.value.map((t) => t?.name ?? '');
  const csvContent = Papa.unparse(
    {
      fields: [currentHeader, newHeader, newOnRegistrationHeader],
      data: tagNames.map((name) => [name, name, '']),
    },
    { newline: '\r\n' }
  );
  const blob = new Blob(['\uFEFF' + csvContent], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'tag_bulk_upload_template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const closeTagCsvDialog = () => {
  showTagCsvUploadDialog.value = false;
  removeUploadCsvFile();
};

const openCsvUpload = () => {
  csvInput.value?.click();
};

const resetCsvUploadStatus = () => {
  validEntries.value = [];
  isValidationCompleted.value = false;
  if (csvInput.value) {
    csvInput.value.value = '';
  }
  uploadProgress.value = 0;
};

const onDropCsvFile = async (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();

  const file = Array.from(event.dataTransfer?.files || []).find((f) =>
    f.name.toLowerCase().endsWith('.csv')
  );

  if (!file) return;

  await checkCsvFile(file);
};

const onSelectCsvFile = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';

  if (!file) return;
  await checkCsvFile(file);
};

const checkCsvFile = async (file: File) => {
  csvFile.value = file;

  if (!file.name.toLowerCase().endsWith('.csv')) {
    isValidFile.value = false;
    commitAddNotification(store, {
      messageKey: sharedMessages.onlyReceiveCsvFile,
      color: 'error',
    });
    resetCsvUploadStatus();
    return;
  }

  if (file.size > MAX_CSV_FILE_SIZE) {
    isValidFile.value = false;
    commitAddNotification(store, {
      messageKey: sharedMessages.fileSizeExceeded,
      color: 'error',
    });
    resetCsvUploadStatus();
    return;
  }

  isValidFile.value = true;

  await parseCsvData(file);
};

const parseCsvData = async (file: File) => {
  isValidationCompleted.value = false;
  csvFile.value = file;
  validEntries.value = [];
  uploadProgress.value = 0;
  const text = await file.text();
  const { data, errors } = Papa.parse<TagCsvRow>(text, CSV_PARSE_OPTIONS);

  if (errors.length > 0) {
    const primaryError =
      errors.find((e) => e.type === 'Quotes') ??
      errors.find((e) => e.type === 'FieldMismatch');
    const messageKey =
      primaryError?.type === 'Quotes' && primaryError?.code === 'MissingQuotes'
        ? sharedMessages.csvTagBulkMissingQuotes
        : primaryError?.type === 'FieldMismatch' &&
          primaryError?.code === 'TooManyFields'
        ? sharedMessages.csvTagBulkTooManyFields
        : sharedMessages.csvFileUploadFailed;

    commitAddNotification(store, { messageKey, color: 'error' });
    uploadProgress.value = 0;
    isValidFile.value = false;
    return;
  }

  const tagNameLengthErrors: number[] = [];
  const tagNameDuplicationErrors: number[] = [];
  const tagNameTypeErrors: number[] = [];
  const emptyNewTagNameErrors: number[] = [];
  const tagNotFoundErrors: { row: number; tagName: string }[] = [];
  const newTagNameSet = new Set<string>();
  const existingTagNames = new Set(
    tags.value
      .map((t) => t?.name)
      .filter((name): name is string => Boolean(name))
  );

  for (const [index, row] of data.entries()) {
    const csvRowNumber = index + 2;
    const newOnRegistration = (
      row?.[NEW_ON_REGISTRATION_HEADER_JA] ??
      row?.[NEW_ON_REGISTRATION_HEADER_EN] ??
      ''
    )
      .trim()
      .toLowerCase();
    const isNewRegistration =
      newOnRegistration === '新規' || newOnRegistration === 'new';
    const currentTagName = isNewRegistration
      ? ''
      : (
          row?.[CURRENT_TAG_HEADER_JA] ??
          row?.[CURRENT_TAG_HEADER_EN] ??
          ''
        ).trim();
    const newTagName = (
      row?.[NEW_TAG_HEADER_JA] ??
      row?.[NEW_TAG_HEADER_EN] ??
      ''
    ).trim();

    if (!newTagName) {
      emptyNewTagNameErrors.push(csvRowNumber);
      continue;
    }

    if (currentTagName === newTagName) continue;

    if (currentTagName && !existingTagNames.has(currentTagName)) {
      tagNotFoundErrors.push({ row: csvRowNumber, tagName: currentTagName });
      continue;
    }

    if (newTagName.includes(',')) {
      tagNameTypeErrors.push(csvRowNumber);
      continue;
    }

    if (newTagNameSet.has(newTagName)) {
      tagNameDuplicationErrors.push(csvRowNumber);
      continue;
    }

    if (isValidLength(45)(newTagName) !== true) {
      tagNameLengthErrors.push(csvRowNumber);
      continue;
    }

    validEntries.value.push({ currentTagName, newTagName });
    newTagNameSet.add(newTagName);
  }

  uploadProgress.value = 100;

  notifyCsvRowsError(tagNameLengthErrors, sharedMessages.sdsTagNameIsNotOver45);
  notifyCsvRowsError(
    tagNameDuplicationErrors,
    sharedMessages.alreadyExistingTags
  );
  notifyCsvRowsError(tagNameTypeErrors, sharedMessages.tagCsvWrongTagType);
  notifyCsvRowsError(
    emptyNewTagNameErrors,
    sharedMessages.tagCsvNewTagNameEmpty
  );

  if (tagNotFoundErrors.length > 0) {
    const { row, tagName } = tagNotFoundErrors[0];
    commitAddNotification(store, {
      messageKey: sharedMessages.tagCsvCurrentTagNotFound,
      messageValues: { row: String(row), tagName },
      color: 'error',
    });
  }

  const hasNoValidationErrors =
    tagNameLengthErrors.length === 0 &&
    tagNameDuplicationErrors.length === 0 &&
    tagNameTypeErrors.length === 0 &&
    emptyNewTagNameErrors.length === 0 &&
    tagNotFoundErrors.length === 0;
  isValidationCompleted.value = hasNoValidationErrors;

  if (validEntries.value.length === 0 && isValidationCompleted.value) {
    commitAddNotification(store, {
      messageKey: sharedMessages.tagCsvNoChanges,
      color: 'error',
    });
  }
};

const submitCsvData = async () => {
  try {
    const bulkUpdateTags = validEntries.value.map((entry) => ({
      currentTagName: entry.currentTagName,
      newTagName: entry.newTagName,
    }));

    await dispatchBulkCreateCompanyTag(store, {
      payload: {
        tags: bulkUpdateTags,
      },
      companyId: readAssertedCompanyId(store),
    });

    await getCompanyTag();
  } catch (error) {
    console.error(error);

    if (error instanceof AxiosError) {
      commitAddNotification(store, {
        messageKey: sharedMessages.responseError,
        messageValues: {
          detail: error.response?.data ?? error.message,
        },
        color: 'error',
      });
    } else {
      commitAddNotification(store, {
        messageKey: sharedMessages.genericError,
        color: 'error',
      });
    }
  } finally {
    showTagCsvUploadDialog.value = false;
    removeUploadCsvFile();
  }
};

const removeUploadCsvFile = () => {
  csvFile.value = null;
  resetCsvUploadStatus();
};

onMounted(async () => {
  await getCompanyTag();
});

return { __sfc: true,CURRENT_TAG_HEADER_JA, NEW_TAG_HEADER_JA, NEW_ON_REGISTRATION_HEADER_JA, CURRENT_TAG_HEADER_EN, NEW_TAG_HEADER_EN, NEW_ON_REGISTRATION_HEADER_EN, MAX_CSV_FILE_SIZE, CSV_PARSE_OPTIONS, m, formatErrorRowNumbers, notifyCsvRowsError, isLoading, tags, getCompanyTag, hasPermission, P, csvFile, showTagFormDialog, showTagCsvUploadDialog, uploadProgress, isValidFile, validEntries, csvInput, isValidationCompleted, formatFileSize, downloadCSV, closeTagCsvDialog, openCsvUpload, resetCsvUploadStatus, onDropCsvFile, onSelectCsvFile, checkCsvFile, parseCsvData, submitCsvData, removeUploadCsvFile, CompanyTagsTable, CompanyTagFormDialog, sharedMessages }
}

})